/**
 * The permission catalogue is fixed in code, not in the database.
 *
 * Roles are administrator-defined and stored in Aurora so entitlements can be
 * reconfigured without a deployment, but the set of things a role *can* grant
 * is a closed list that only changes through change-managed code review. This
 * is what stops an administrator inventing a permission that no control maps
 * to, and it gives the SOX control matrix a stable set of identifiers.
 */

export const PERMISSION_CATEGORIES = [
  'query',
  'api',
  'approval',
  'administration',
  'audit',
] as const;

export type PermissionCategory = (typeof PERMISSION_CATEGORIES)[number];

/** Drives UI treatment and whether the approval engine considers the action privileged. */
export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

export interface PermissionDefinition {
  readonly key: PermissionKey;
  readonly label: string;
  readonly description: string;
  readonly category: PermissionCategory;
  readonly risk: RiskLevel;
  /**
   * Privileged permissions cannot be granted globally: a role holding one is
   * only effective against connection targets explicitly listed in a
   * target_grant row. Enforced in resolveEntitlements().
   */
  readonly requiresTargetScope: boolean;
}

export const PERMISSIONS = {
  // ---------------------------------------------------------------- query
  'query.execute.read': {
    key: 'query.execute.read',
    label: 'Run read-only queries',
    description:
      'Execute SELECT statements against granted database targets using the read-only service account.',
    category: 'query',
    risk: 'low',
    requiresTargetScope: true,
  },
  'query.execute.write': {
    key: 'query.execute.write',
    label: 'Run data-modifying statements',
    description:
      'Submit INSERT, UPDATE, DELETE or DDL against granted targets. Always routed through approval and executed with the write service account.',
    category: 'query',
    risk: 'critical',
    requiresTargetScope: true,
  },
  'query.execute.proc': {
    key: 'query.execute.proc',
    label: 'Execute stored procedures',
    description:
      'Invoke stored procedures on granted targets. Procedures outside the per-target allowlist require approval.',
    category: 'query',
    risk: 'high',
    requiresTargetScope: true,
  },
  'query.schema.browse': {
    key: 'query.schema.browse',
    label: 'Browse schema metadata',
    description:
      'Read table, view, column and procedure metadata for granted targets to drive editor autocomplete.',
    category: 'query',
    risk: 'low',
    requiresTargetScope: true,
  },
  'query.saved.manage': {
    key: 'query.saved.manage',
    label: 'Manage saved scripts',
    description:
      'Save, version, tag and archive your own scripts, and share one with a role you hold.',
    category: 'query',
    risk: 'low',
    requiresTargetScope: false,
  },
  /**
   * The compliance half of the script library.
   *
   * `high` despite granting no access to any data, which is worth being
   * explicit about. Publishing a script cannot run anything: whoever runs it
   * still needs their own `query.execute.*` on the target, their own
   * justification, and their own approval if it writes.
   *
   * What it does is direct other people's privileges. A script titled
   * "standard month-end reconciliation check" that quietly reads a table
   * nobody would have thought to open is refused by nothing downstream,
   * because nothing downstream is wrong -- the person running it has every
   * permission the statement needs. The approver reading the statement is
   * the only control in that path, which is what makes this a high-risk
   * grant.
   *
   * Deliberately not in TOXIC_COMBINATIONS alongside `query.saved.manage`.
   * Requester-cannot-approve is enforced in code on every decision, the same
   * way it is for `approval.grant`, so holding both is no more dangerous
   * than being able to submit a job and approve other people's.
   */
  'query.saved.publish': {
    key: 'query.saved.publish',
    label: 'Publish organisation-wide scripts',
    description:
      'Approve publication of a script to everyone, and withdraw a published script.',
    category: 'query',
    risk: 'high',
    requiresTargetScope: false,
  },

  // ------------------------------------------------------------------ api
  'api.request.send': {
    key: 'api.request.send',
    label: 'Send safe API requests',
    description:
      'Issue GET, HEAD and OPTIONS requests to approved endpoints using managed secrets.',
    category: 'api',
    risk: 'low',
    requiresTargetScope: true,
  },
  'api.request.mutate': {
    key: 'api.request.mutate',
    label: 'Send mutating API requests',
    description:
      'Issue POST, PUT, PATCH or DELETE to approved endpoints. Production endpoints always require approval.',
    category: 'api',
    risk: 'critical',
    requiresTargetScope: true,
  },
  'api.collection.manage': {
    key: 'api.collection.manage',
    label: 'Manage API collections',
    description: 'Create and edit API collections, requests and environment variable sets.',
    category: 'api',
    risk: 'medium',
    requiresTargetScope: false,
  },

  // ------------------------------------------------------------- approval
  'approval.grant': {
    key: 'approval.grant',
    label: 'Approve requests',
    description:
      'Approve or reject submitted requests. Never effective on the holder\u2019s own submissions.',
    category: 'approval',
    risk: 'critical',
    requiresTargetScope: true,
  },
  'approval.rule.manage': {
    key: 'approval.rule.manage',
    label: 'Manage approval rules',
    description:
      'Configure which request classes require approval and how many approvers are needed.',
    category: 'approval',
    risk: 'critical',
    requiresTargetScope: false,
  },

  // -------------------------------------------------------- administration
  'target.manage': {
    key: 'target.manage',
    label: 'Manage connection targets',
    description: 'Register, edit and disable database servers and API endpoint definitions.',
    category: 'administration',
    risk: 'high',
    requiresTargetScope: false,
  },
  'secret.bind': {
    key: 'secret.bind',
    label: 'Bind managed secrets',
    description:
      'Associate a Secrets Manager secret ARN with a connection target. Secret values are never readable through the tool.',
    category: 'administration',
    risk: 'critical',
    requiresTargetScope: false,
  },
  'role.manage': {
    key: 'role.manage',
    label: 'Manage roles and permissions',
    description: 'Create roles, assign permissions and scope roles to connection targets.',
    category: 'administration',
    risk: 'critical',
    requiresTargetScope: false,
  },
  'user.manage': {
    key: 'user.manage',
    label: 'Manage users',
    description: 'Assign and revoke roles for users, and disable user accounts.',
    category: 'administration',
    risk: 'critical',
    requiresTargetScope: false,
  },
  'job.view.all': {
    key: 'job.view.all',
    label: 'View all jobs',
    description:
      'See jobs submitted by any user, not just your own. Result payloads still require a download grant.',
    category: 'administration',
    risk: 'medium',
    requiresTargetScope: false,
  },
  'job.cancel.all': {
    key: 'job.cancel.all',
    label: 'Cancel any job',
    description: 'Cancel a running or queued job submitted by another user.',
    category: 'administration',
    risk: 'medium',
    requiresTargetScope: false,
  },

  // ---------------------------------------------------------------- audit
  'audit.read': {
    key: 'audit.read',
    label: 'Read the audit ledger',
    description: 'Search and view audit events across all users.',
    category: 'audit',
    risk: 'medium',
    requiresTargetScope: false,
  },
  'audit.export': {
    key: 'audit.export',
    label: 'Export audit evidence',
    description:
      'Produce signed evidence packs and entitlement snapshots for auditors and access reviews.',
    category: 'audit',
    risk: 'high',
    requiresTargetScope: false,
  },
} as const satisfies Record<string, Omit<PermissionDefinition, 'key'> & { key: string }>;

export type PermissionKey = keyof typeof PERMISSIONS;

export const PERMISSION_KEYS = Object.keys(PERMISSIONS) as PermissionKey[];

export function isPermissionKey(value: string): value is PermissionKey {
  return Object.prototype.hasOwnProperty.call(PERMISSIONS, value);
}

export function permissionDefinition(key: PermissionKey): PermissionDefinition {
  return PERMISSIONS[key] as PermissionDefinition;
}

export function permissionsByCategory(category: PermissionCategory): PermissionDefinition[] {
  return PERMISSION_KEYS.map(permissionDefinition).filter((p) => p.category === category);
}

/**
 * Segregation of duties.
 *
 * A single role holding both halves of any pair below lets one person both
 * request and authorise a privileged change. The admin UI blocks or warns on
 * these, and the nightly entitlement report lists every user who holds a pair
 * across the union of their roles -- which is the case auditors actually ask
 * about, since SoD is violated by the person, not by the role.
 */
export interface ToxicCombination {
  readonly a: PermissionKey;
  readonly b: PermissionKey;
  readonly severity: 'block' | 'warn';
  readonly rationale: string;
}

export const TOXIC_COMBINATIONS: readonly ToxicCombination[] = [
  {
    a: 'approval.grant',
    b: 'query.execute.write',
    severity: 'block',
    rationale:
      'The holder could submit a data-modifying statement and approve it themselves. Maker and checker must be different people.',
  },
  {
    a: 'approval.grant',
    b: 'api.request.mutate',
    severity: 'block',
    rationale:
      'The holder could submit a mutating API call to production and approve it themselves.',
  },
  {
    a: 'approval.grant',
    b: 'approval.rule.manage',
    severity: 'block',
    rationale:
      'The holder could remove the approval requirement for a class of request and then act on it unchecked.',
  },
  {
    a: 'role.manage',
    b: 'approval.grant',
    severity: 'warn',
    rationale:
      'The holder could grant themselves an execution permission and then approve their own use of it. Acceptable only for a small, monitored platform-administrator group.',
  },
  {
    a: 'role.manage',
    b: 'audit.read',
    severity: 'warn',
    rationale:
      'Concentrates entitlement change and audit visibility in one person. Prefer a separate audit-reader role held by compliance.',
  },
  {
    a: 'secret.bind',
    b: 'query.execute.write',
    severity: 'warn',
    rationale:
      'The holder could repoint a target at a different credential and then write through it.',
  },
];

/** Every toxic combination present in a set of permissions. */
export function findToxicCombinations(permissions: Iterable<PermissionKey>): ToxicCombination[] {
  const held = new Set(permissions);
  return TOXIC_COMBINATIONS.filter((c) => held.has(c.a) && held.has(c.b));
}

/**
 * Permissions that always route through the approval engine regardless of how
 * a rule is configured. Keeping this in code means an administrator cannot
 * disable maker-checker on the highest-risk actions by editing a rules table.
 */
export const ALWAYS_REQUIRES_APPROVAL: readonly PermissionKey[] = [
  'query.execute.write',
  'api.request.mutate',
];

export function alwaysRequiresApproval(key: PermissionKey): boolean {
  return ALWAYS_REQUIRES_APPROVAL.includes(key);
}
